import React from 'react'
import { Text, View, StyleSheet } from '@react-pdf/renderer'
import { ArcedDiff } from '../gauges/ArcedDiff'

const styles = StyleSheet.create({
  tableHeaderTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableHeaderTitleText1: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: 500,
    marginRight: 5,
  },
})

const OverviewGaugesDiff = ({ coverData, categoriesData, uiData, quinaryScale, add2GaugePage, avgPoint }) => {
  const comparisonValues = add2GaugePage && Array.isArray(avgPoint)
    ? avgPoint
      .slice(0, 4)
      .map(value => Number(value))
      .filter(value => !Number.isNaN(value))
    : [];

  const hasComparisonTeams =
    add2GaugePage &&
    comparisonValues.length >= 2;

  const overview = coverData?.overview ?? {};
  const overviewLabels = [
    overview.currentYear,
    overview.prevYear,
    overview.thirdYear,
    overview.fourthYear,
  ].filter(label => typeof label === 'string' && label.trim().length > 0);

  const profileLabels = Array.isArray(coverData?.profile)
    ? coverData.profile.filter(label => typeof label === 'string' && label.trim().length > 0)
    : [];

  const comparisonSeries = comparisonValues.map((value, index) => ({
    label: overviewLabels[index] || profileLabels[index] || `${uiData.profile || 'Profile'} ${index + 1}`,
    value,
  }));

  let mainValue;
  let secondaryValue;
  let mainColor;
  let secondaryColor;

  if (hasComparisonTeams) {
    mainValue = Number(comparisonSeries[0]?.value ?? 0);
    secondaryValue = Number(comparisonSeries[1]?.value ?? 0);
    mainColor = '#fff';
    secondaryColor = '#000';
  } else {
    const numericAvg =
      typeof avgPoint === 'number'
        ? avgPoint
        : Number(coverData?.overview?.avgPoint ?? 0);

    mainValue = numericAvg;
    secondaryValue = numericAvg;
    mainColor = '#fff';
    secondaryColor = '#000';
  }

  return (
    <>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          paddingLeft: '5%',
          paddingRight: '5%',
          paddingTop: 5,
          paddingBottom: 5,
          backgroundColor: 'rgb(226 232 240)',
        }}
      >
        <View style={styles.tableHeaderTitle}>
          <Text style={styles.tableHeaderTitleText1}>{uiData.features}</Text>
        </View>
      </View>

      <View
        style={{
          position: 'relative',
          paddingLeft: '5%',
          paddingRight: '5%',
          paddingTop: 20,
        }}
      >
        <View
          style={{
            backgroundColor: '#006cb7',
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: hasComparisonTeams ? 'flex-start' : 'center',
            alignItems: 'center',
            padding: 20,
            color: '#fff',
            height: 160,
          }}
        >
          <View
            id="avgGauge"
            style={{ position: 'relative', width: 140, height: 140, overflow: 'visible' }}
          >
            <View>
              <View
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 5,
                  justifyContent: 'center',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 30,
                    fontWeight: 700,
                  }}
                >
                  {mainValue}
                </Text>
              </View>
              <ArcedDiff
                value01={mainValue}
                color01={mainColor}
                value02={secondaryValue}
                color02={secondaryColor}
                quinaryScale={quinaryScale}
                prevOpacity={hasComparisonTeams ? 0.4 : 1}
              />
            </View>
          </View>

          <View style={{ paddingLeft: 20 }}>
            <Text
              style={{
                fontFamily: 'Inter',
                fontSize: 18,
                fontWeight: 600,
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              {hasComparisonTeams ? uiData.averageGauge : uiData.average}
            </Text>
            {hasComparisonTeams && (
              <View
                style={{
                  fontFamily: 'Inter',
                  fontSize: 11,
                  fontWeight: 500,
                  width: 320,
                }}
              >
                {comparisonSeries.map((series, index) => (
                  <Text
                    key={`overview-comparison-${index}`}
                    style={{
                      marginBottom: index === comparisonSeries.length - 1 ? 0 : 4,
                      opacity: index === 0 ? 1 : 0.6,
                    }}
                  >
                    {series.label} {series.value}
                  </Text>
                ))}
              </View>
            )}
          </View>
        </View>

        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 10,
            paddingTop: 15,
            paddingBottom: 15,
          }}
        >
          {categoriesData.map(entry => {
            const points = Array.isArray(entry.points) ? entry.points : [0, 0]
            const pointsForComparison = comparisonSeries.map((_, index) => points[index] ?? '-')
            const pointsForGauge = comparisonSeries.map((_, index) => Number(points[index] ?? 0))
            const currentValue = hasComparisonTeams ? pointsForGauge[0] : (add2GaugePage ? points[0] : points[1])
            const prevValue = hasComparisonTeams ? pointsForGauge[1] : (add2GaugePage ? points[1] : points[0])

            return (
              <View key={entry.id}>
                <View
                  style={{
                    height: 120,
                    width: 262,
                    backgroundColor: '#fff',
                    padding: 5,
                    borderRadius: 5,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <View
                    style={{
                      position: 'relative',
                      width: '42%',
                      padding: 5,
                    }}
                  >
                    <View
                      style={{
                        position: 'absolute',
                        bottom: 84,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        justifyContent: 'center',
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 18,
                          fontWeight: 700,
                        }}
                      >
                        {currentValue}
                      </Text>
                    </View>
                    <ArcedDiff
                      value01={currentValue}
                      color01={'#000'}
                      value02={prevValue}
                      color02={'#999'}
                      quinaryScale={quinaryScale}
                    />
                  </View>
                  <View
                    style={{
                      width: '58%',
                      paddingLeft: 10,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: 'Inter',
                        fontSize: 12,
                        fontWeight: 500,
                        maxWidth: '90%',
                      }}
                    >
                      {entry.name}
                    </Text>
                    {hasComparisonTeams && (
                      <View
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 8,
                          fontWeight: 400,
                          width: 130,
                          marginTop: 5
                        }}
                      >
                        {comparisonSeries.map((series, index) => (
                          <Text
                            key={`category-comparison-${entry.id}-${index}`}
                            style={{
                              marginBottom: index === comparisonSeries.length - 1 ? 0 : 2,
                              color: index === 0 ? '#000' : '#666',
                            }}
                          >
                            {series.label} <Text style={{ fontWeight: "bold" }}>{pointsForComparison[index]}</Text>
                          </Text>
                        ))}
                      </View>
                    )}
                  </View>
                </View>
              </View>
            )
          })}
        </View>

        <View
          style={{
            width: '100%',
            paddingTop: 0,
          }}
        >
          <Text
            style={{
              fontFamily: 'Inter',
              fontSize: 8,
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {uiData.diffNote}
          </Text>
        </View>
      </View>
    </>
  )
}

export default OverviewGaugesDiff
